// This is the only place where we allow it
// eslint-disable-next-line no-restricted-imports
import axios, {
  AxiosInstance,
  RawAxiosRequestHeaders,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { getCookie } from "@/utils/cookies";
import { FEDERATION_TOKEN_COOKIE_KEY } from "@/config/cookiesKeys";
import { trackFailedRequest } from "@/components/BugReport/failedRequestTracker";

export const TWO_FACTOR_COMPLETED_EVENT = "twoFACompleted";
export const TWO_FACTOR_NEEDED_EVENT = "twoFANeeded";

// The CI pipeline id baked in at build time, which is also the Sentry release.
const BUILD = process.env.NEXT_PUBLIC_SENTRY_RELEASE || "dev";

type AxiosRequestConfigWithApiVersion = AxiosRequestConfig & {
  apiVersion?: "v2" | "v3";
};

class WebhsareApi {
  private baseUrl = process.env.NEXT_PUBLIC_BACKEND;
  private axiosInstance: AxiosInstance;
  private headerConfig: RawAxiosRequestHeaders;
  private AuthToken?: string;
  private FederationToken?: string;

  // This is needed in case we get a response from 2FA, so we can retry the requests once it succeeds
  private pendingRequests: Array<{
    config: AxiosRequestConfigWithApiVersion;
    resolve: (value: AxiosResponse) => void;
    reject: (reason?: any) => void;
  }> = [];
  private is2FAInProgress = false;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: this.baseUrl,
      // The browser owns User-Agent and drops any value we set, so the channel
      // travels in its own header, the way the AWS and Azure SDKs do it.
      headers: { "X-Webshare-User-Agent": `WebshareDashboard/${BUILD}` },
    });
    this.axiosInstance.defaults.withCredentials = true;
    this.headerConfig = this.axiosInstance.defaults.headers.common;

    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig & { apiVersion?: string }) => {
        const apiVersion = config.apiVersion || "v2";
        if (config.url?.startsWith("/")) {
          config.url = `${apiVersion}${config.url}`;
        } else {
          config.url = `${apiVersion}/${config.url}`;
        }
        config.headers.set({
          ...this.headerConfig,
          ...config.headers,
        });
        return config;
      },
      (error) => Promise.reject(error)
    );
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (axios.isAxiosError(error) && error.config && error.response) {
          const start = (error.config as any)._startTime;
          trackFailedRequest({
            method: (error.config.method ?? "GET").toUpperCase(),
            url: error.config.url ?? "",
            status: error.response.status,
            duration: start ? Date.now() - start : 0,
          });
        }
        return Promise.reject(error);
      }
    );

    this.axiosInstance.interceptors.request.use((config) => {
      (config as any)._startTime = Date.now();
      return config;
    });

    // We need this part to avoid calling backend without when we already have it inside cookies
    const cookie = getCookie(FEDERATION_TOKEN_COOKIE_KEY);
    if (cookie && String(cookie).trim() !== "") {
      this.setFederationToken(String(cookie));
    }

    this.listen2FACompletedEvent();
  }

  public setAuthToken(authToken: string) {
    this.headerConfig = {
      ...this.headerConfig,
      Authorization: "Token " + authToken,
    };
    this.AuthToken = authToken;
  }

  public removeFederationToken() {
    this.FederationToken = undefined;
    this.headerConfig = {
      ...this.headerConfig,
    };
    delete this.headerConfig["X-Webshare-Federated-Access"];
  }

  public setFederationToken(userId: string) {
    this.headerConfig = {
      ...this.headerConfig,
      "X-Webshare-Federated-Access": userId,
    };
    this.FederationToken = userId;
  }

  public setSubuserHeader(subUserId: string) {
    this.headerConfig = {
      ...this.headerConfig,
      "X-Subuser": subUserId,
    };
  }

  public removeSubUserHeader() {
    delete this?.headerConfig?.["X-Subuser"];
  }

  public getAuthToken() {
    return this.AuthToken;
  }

  public getFederationToken() {
    return this.FederationToken;
  }

  public getSubUserId() {
    return this?.headerConfig?.["X-Subuser"] as string;
  }

  public removeAuthToken() {
    delete this.headerConfig.Authorization;
  }

  private dispatch2FANeededEvent() {
    const event = new CustomEvent(TWO_FACTOR_NEEDED_EVENT);
    window.dispatchEvent(event);
  }

  private listen2FACompletedEvent() {
    // On server side we do not listen to 2FA events
    if (typeof window === "undefined") return;
    window.addEventListener(TWO_FACTOR_COMPLETED_EVENT, () => {
      this.retryPendingRequests();
    });
  }

  private async retryPendingRequests() {
    const requests = [...this.pendingRequests];
    this.pendingRequests = [];
    this.is2FAInProgress = false;

    for (const { config, resolve, reject } of requests) {
      try {
        const response = await this.axiosInstance.request(config);
        resolve(response);
      } catch (error) {
        reject(error);
      }
    }
  }

  private async handleRequest<T = any>(
    config: AxiosRequestConfigWithApiVersion
  ): Promise<AxiosResponse> {
    try {
      return await this.axiosInstance.request<T>(config);
    } catch (error: unknown) {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 403 &&
        error.response?.data?.code === "2fa_needed"
      ) {
        if (!this.is2FAInProgress) {
          this.is2FAInProgress = true;
          this.dispatch2FANeededEvent();
        }
        return new Promise((resolve, reject) => {
          this.pendingRequests.push({ config, resolve, reject });
        });
      }
      throw error;
    }
  }

  public async get<T = any>(
    url: string,
    config?: AxiosRequestConfigWithApiVersion
  ): Promise<AxiosResponse<T>> {
    return this.handleRequest<T>({ ...config, method: "get", url });
  }

  public async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfigWithApiVersion
  ): Promise<AxiosResponse<T>> {
    return this.handleRequest<T>({ ...config, method: "post", url, data });
  }

  public async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfigWithApiVersion
  ): Promise<AxiosResponse<T>> {
    return this.handleRequest<T>({ ...config, method: "patch", url, data });
  }

  public async delete<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfigWithApiVersion
  ): Promise<AxiosResponse<T>> {
    return this.handleRequest<T>({ ...config, method: "delete", url, data });
  }
}

const webshareAPI = new WebhsareApi();
export default webshareAPI;
